import { useEffect, useMemo } from 'react';
import cx from 'classnames';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';

import { MIN_WORDS_FOR_PLAGIARISM_CHECK } from 'src/data/insights';
import { formatUpdatedDateTime } from 'src/lib/format';
import { countWords } from 'src/lib/string';

import { InsightBox } from '../InsightBox';

import { FirstTimeButton } from './components/FirstTimeButton';
import { Loader } from './components/Loader';
import { PlagiarismCheckResults } from './components/PlagiarismCheckResults';
import { PlagiarismNotFound } from './components/PlagiarismNotFound';
import { ResultsFoundTitle } from './components/ResultsFoundTitle';
import { useStyles } from './useStyles';

export const PlagiarismChecker = ({
  copyText,
  plagiarismChecksUsed,
  plagiarismChecksLimit,
  loading,
  lastPlagiarismCheckTime,
  hasPerformedPlagiarismCheck,
  potentialCopiedSentences,
  onCheckPlagiarism,
  isInsightsPanelOpen,
  onOpenPlagiarism,
}) => {
  const styles = useStyles();
  const wordsCountDisable = countWords(copyText) < MIN_WORDS_FOR_PLAGIARISM_CHECK;
  const hasURLsPlagiarized = !isEmpty(potentialCopiedSentences);
  const hasMultipleURLsPlagiarized = potentialCopiedSentences?.length > 1;
  const numOfResults = potentialCopiedSentences?.length;

  const toolTip = wordsCountDisable
    ? `Plagiarism check requires a minimum of ${MIN_WORDS_FOR_PLAGIARISM_CHECK} words.`
    : '';

  const formattedLastCheckTime = formatUpdatedDateTime(lastPlagiarismCheckTime);

  const showFirstTimeButton = !hasPerformedPlagiarismCheck && !loading;
  const showResultsNumber = hasPerformedPlagiarismCheck && !loading && hasURLsPlagiarized;
  const showNotFound = hasPerformedPlagiarismCheck && !loading && !hasURLsPlagiarized;
  const showFirstResult = hasPerformedPlagiarismCheck && !loading && hasURLsPlagiarized;

  const subtitle = useMemo(
    () =>
      loading ? (
        'Scanning...'
      ) : (
        <>
          <span>
            {hasPerformedPlagiarismCheck
              ? `Last checked: ${formattedLastCheckTime}`
              : `Scan your content to ensure it's original`}
          </span>
          <span>
            {plagiarismChecksLimit
              ? `${plagiarismChecksUsed}/${plagiarismChecksLimit} checks used`
              : ''}
          </span>
        </>
      ),
    [
      loading,
      hasPerformedPlagiarismCheck,
      formattedLastCheckTime,
      plagiarismChecksUsed,
      plagiarismChecksLimit,
    ]
  );

  useEffect(() => {
    if (isInsightsPanelOpen) {
      onOpenPlagiarism();
    }
  }, [isInsightsPanelOpen, onOpenPlagiarism]);

  return (
    <InsightBox
      classes={{ subtitle: styles.subtitle }}
      title="Content Originality"
      subtitle={subtitle}
      collapsableContent={
        hasPerformedPlagiarismCheck &&
        !loading &&
        hasMultipleURLsPlagiarized && (
          <PlagiarismCheckResults
            classes={{ resultBox: styles.resultBox }}
            plagiarismCheckResults={potentialCopiedSentences?.slice(1) || []}
          />
        )
      }
    >
      {showResultsNumber && (
        <ResultsFoundTitle
          classes={{
            checkAgain: styles.checkAgain,
            checkAgainIcon: styles.checkAgainIcon,
            disabled: styles.disabled,
          }}
          className={cx(styles.resultBox, styles.row, styles.firstBox)}
          numOfResults={numOfResults}
          toolTip={toolTip}
          onCheckPlagiarism={onCheckPlagiarism}
          disabled={wordsCountDisable}
        />
      )}
      {showFirstTimeButton && (
        <FirstTimeButton
          classes={{
            subtitle: styles.subtitle,
          }}
          className={styles.row}
          wordsCountDisable={wordsCountDisable}
          toolTip={toolTip}
          onCheckPlagiarism={onCheckPlagiarism}
          disabled={wordsCountDisable}
        />
      )}
      {showNotFound && (
        <PlagiarismNotFound
          className={cx(styles.resultBox, styles.success, styles.row, styles.firstBox)}
          classes={{
            checkAgain: styles.checkAgain,
            checkAgainIcon: styles.checkAgainIcon,
            disabled: styles.disabled,
          }}
          wordsCountDisable={wordsCountDisable}
          toolTip={toolTip}
          onCheckPlagiarism={onCheckPlagiarism}
          disabled={wordsCountDisable}
        />
      )}
      {loading && (
        <div className={styles.resultBox}>
          <Loader />
        </div>
      )}
      {showFirstResult && (
        <PlagiarismCheckResults
          classes={{ resultBox: styles.resultBox }}
          plagiarismCheckResults={
            potentialCopiedSentences?.length > 0 ? [potentialCopiedSentences[0]] : []
          }
        />
      )}
    </InsightBox>
  );
};

PlagiarismChecker.propTypes = {
  plagiarismChecksUsed: PropTypes.number,
  plagiarismChecksLimit: PropTypes.number,
  loading: PropTypes.bool,
  lastPlagiarismCheckTime: PropTypes.string,
  hasPerformedPlagiarismCheck: PropTypes.bool,
  potentialCopiedSentences: PropTypes.array,
  onCheckPlagiarism: PropTypes.func.isRequired,
  copyText: PropTypes.string,
  isInsightsPanelOpen: PropTypes.bool.isRequired,
  onOpenPlagiarism: PropTypes.func.isRequired,
};

PlagiarismChecker.defaultProps = {
  plagiarismChecksUsed: 0,
  plagiarismChecksLimit: 0,
  loading: false,
  lastPlagiarismCheckTime: '',
  hasPerformedPlagiarismCheck: false,
  potentialCopiedSentences: [],
  copyText: '',
};
